import { create } from 'zustand';

import type { SessionExpiryReason, SessionStatus } from './types';

export interface SessionStoreState<TUser> {
  status: SessionStatus;
  user: TUser | null;
  expiryReason: SessionExpiryReason;
  sessionCreatedAt: number | null;
  maxAllowedTime: number | null;
  bannerDismissed: boolean;
  modalShownAt: number | null;

  setActive: (user: TUser, sessionCreatedAt: number, maxAllowedTime: number) => void;
  setInactiveWarning: () => void;
  setMaxAgeWarning: () => void;
  setExpired: (reason: SessionExpiryReason) => void;
  setUnauthenticated: () => void;
  dismissBanner: () => void;
  resetBannerDismissed: () => void;
  resetActivity: () => void;
}

export type SessionStore<TUser> = ReturnType<typeof createSessionStore<TUser>>;

/**
 * One zustand session store per app, generic over the signed-in principal
 * (dashboard: the admin user; onboarding: the invitation principal). The state
 * machine: initializing → active ⇄ inactive_warning → expired, with
 * max_age_warning overriding inactive_warning, and unauthenticated as the
 * signed-out terminal.
 */
export function createSessionStore<TUser>() {
  return create<SessionStoreState<TUser>>()((set) => ({
    status: 'initializing',
    user: null,
    expiryReason: null,
    sessionCreatedAt: null,
    maxAllowedTime: null,
    bannerDismissed: false,
    modalShownAt: null,

    setActive: (user, sessionCreatedAt, maxAllowedTime) =>
      set({
        status: 'active',
        user,
        sessionCreatedAt,
        maxAllowedTime,
        expiryReason: null,
        bannerDismissed: false,
        modalShownAt: null,
      }),

    setInactiveWarning: () =>
      set((state) => ({
        status: state.status === 'active' ? 'inactive_warning' : state.status,
        bannerDismissed: false,
      })),

    setMaxAgeWarning: () =>
      set((state) => ({
        status:
          state.status === 'active' || state.status === 'inactive_warning'
            ? 'max_age_warning'
            : state.status,
        bannerDismissed: false,
      })),

    setExpired: (reason) =>
      set({
        status: 'expired',
        expiryReason: reason,
        modalShownAt: Date.now(),
      }),

    setUnauthenticated: () =>
      set({
        status: 'unauthenticated',
        user: null,
        expiryReason: null,
        sessionCreatedAt: null,
        maxAllowedTime: null,
        bannerDismissed: false,
        modalShownAt: null,
      }),

    dismissBanner: () => set({ bannerDismissed: true }),
    resetBannerDismissed: () => set({ bannerDismissed: false }),
    resetActivity: () =>
      set((state) => ({
        status: state.status === 'inactive_warning' ? 'active' : state.status,
        bannerDismissed: false,
      })),
  }));
}
